import * as Sentry from "@sentry/react";

// Re-export the ErrorBoundary component for React components
export { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";

// Re-export isSentryEnabled using the SDK's isInitialized check
export const isSentryEnabled: boolean = Sentry.isInitialized?.() ?? false;

/**
 * Ensure an unknown value is converted to a proper Error object.
 * Sentry.captureException expects Error, but we accept unknown.
 */
function toError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === "string") {
    return new Error(value);
  }
  return new Error(String(value));
}

/**
 * Capture an error/exception and send it to Sentry.
 * Safe no-op if Sentry is not initialized.
 *
 * Supports special keys in context for tags:
 * - Any key prefixed with "tag_" will be set as a Sentry tag (e.g., tag_boundary -> boundary)
 * - Remaining keys go to extra context
 *
 * @param error - The error to capture (any type)
 * @param context - Optional key-value data to attach as extra or tags
 */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  const sentryError = toError(error);

  Sentry.withScope((scope) => {
    if (context && Object.keys(context).length > 0) {
      // Separate tags (keys starting with "tag_") from extra context
      const tags: Record<string, string> = {};
      const extra: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(context)) {
        if (key.startsWith("tag_")) {
          const tagKey = key.slice(4); // Remove "tag_" prefix
          tags[tagKey] = String(value);
        } else {
          extra[key] = value;
        }
      }

      // Set tags
      for (const [key, value] of Object.entries(tags)) {
        scope.setTag(key, value);
      }

      // Set extra context
      if (Object.keys(extra).length > 0) {
        scope.setContext("error_context", extra);
      }
    }

    Sentry.captureException(sentryError);
  });
}

/**
 * Capture a warning message and send it to Sentry.
 * Safe no-op if Sentry is not initialized.
 *
 * @param message - The warning message
 * @param context - Optional key-value data to attach as extra
 */
export function captureWarning(
  message: string,
  context?: Record<string, unknown>
): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  if (context && Object.keys(context).length > 0) {
    Sentry.withScope((scope) => {
      scope.setContext("warning_context", context);
      Sentry.captureMessage(message, "warning");
    });
  } else {
    Sentry.captureMessage(message, "warning");
  }
}

/**
 * Set user context for Sentry events.
 * Includes role as a tag for filtering in Sentry UI.
 * Safe no-op if Sentry is not initialized.
 *
 * @param userId - The unique user identifier
 * @param email - Optional user email
 * @param role - Optional user role (added as tag)
 */
export function setUserContext(
  userId: string,
  email?: string,
  role?: string
): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  const user: Sentry.User = {
    id: userId,
  };

  if (email !== undefined && email.length > 0) {
    user.email = email;
  }

  Sentry.setUser(user);

  if (role !== undefined && role.length > 0) {
    Sentry.setTag("user_role", role);
  }
}

/**
 * Clear user context from Sentry.
 * Call this on logout.
 * Safe no-op if Sentry is not initialized.
 */
export function clearUserContext(): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  Sentry.setUser(null);
}

/**
 * Add a breadcrumb to the current Sentry scope.
 * Breadcrumbs help trace the steps leading to an error.
 * Safe no-op if Sentry is not initialized.
 *
 * @param category - Category grouping for the breadcrumb
 * @param message - Human-readable description
 * @param data - Optional structured data
 * @param level - Severity: "fatal", "error", "warning", "info", "debug"
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.Breadcrumb["level"] = "info"
): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  const breadcrumb: Sentry.Breadcrumb = {
    category,
    message,
    level,
    data: data ?? undefined,
  };

  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Set a tag on the current Sentry scope.
 * Tags are indexed and searchable in Sentry.
 * Safe no-op if Sentry is not initialized.
 *
 * @param key - The tag key
 * @param value - The tag value
 */
export function setTag(key: string, value: string): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  Sentry.setTag(key, value);
}

/**
 * Execute a callback with a temporary Sentry scope.
 * Useful for attaching context/data to a specific operation.
 * Safe no-op if Sentry is not initialized.
 *
 * @param callback - Function that receives a Sentry scope
 */
export function withScope(
  callback: (scope: Sentry.Scope) => void
): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  Sentry.withScope(callback);
}

/**
 * Capture a Supabase operation error with structured context.
 * Sets tags for supabase_table and supabase_operation for filtering in Sentry.
 * Excludes auth-related errors (login failures, 401s, token refresh).
 * Safe no-op if Sentry is not initialized.
 *
 * @param error - The error from a Supabase call
 * @param context - Structured context about the failed operation
 */
export function captureSupabaseError(
  error: unknown,
  context: {
    table?: string;
    operation?: "select" | "insert" | "update" | "delete" | "rpc" | "invoke" | string;
    functionName?: string;
    params?: Record<string, unknown>;
    statusCode?: number;
    errorCode?: string;
  }
): void {
  if (!Sentry.isInitialized?.()) {
    return;
  }

  // Exclude auth errors — these are expected user flows, not bugs
  const authErrorCodes = [
    "invalid_credentials",
    "user_not_found",
    "email_not_confirmed",
    "invalid_grant",
    "otp_expired",
    "session_not_found",
    "refresh_token_not_found",
  ];

  if (context.errorCode && authErrorCodes.includes(context.errorCode)) {
    return;
  }
  if (context.statusCode === 401 || context.statusCode === 403) {
    return;
  }
  if (context.table === "auth" || context.functionName?.startsWith("auth.")) {
    return;
  }

  const sentryContext: Record<string, unknown> = {
    ...context,
  };

  if (context.table) {
    sentryContext["tag_supabase_table"] = context.table;
  }
  if (context.operation) {
    sentryContext["tag_supabase_operation"] = context.operation;
  }
  if (context.functionName) {
    sentryContext["tag_supabase_function"] = context.functionName;
  }

  captureError(error, sentryContext);
}

/**
 * Add a Supabase breadcrumb for tracing call history.
 * Logs both successful and failed calls.
 *
 * @param operation - The Supabase operation type
 * @param target - Table name or function name
 * @param status - "ok" or "error"
 * @param data - Optional extra data
 */
export function addSupabaseBreadcrumb(
  operation: string,
  target: string,
  status: "ok" | "error",
  data?: Record<string, unknown>
): void {
  addBreadcrumb(
    "supabase",
    `${operation} ${target}: ${status}`,
    { operation, target, status, ...data },
    status === "error" ? "error" : "info"
  );
}
